import { Phone, Mail, Globe, MapPin } from "lucide-react";
import { useLang } from "@/lang";
import { MaskLines, Reveal, Crosshair } from "@/components/Reveal";
import { PHONE, PHONE_TEL, EMAIL } from "@/content";

export default function Contact() {
    const { c } = useLang();
    return (
        <>
            <section className="relative flex min-h-[60svh] items-end overflow-hidden bg-ink" data-testid="contact-hero">
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-[0.14]" aria-hidden="true">
                    <svg viewBox="0 0 600 600" className="h-[90vmin] w-[90vmin] animate-spin-slow">
                        <circle cx="300" cy="300" r="280" fill="none" stroke="#3CC5F1" strokeWidth="1" strokeDasharray="4 14" />
                    </svg>
                </div>
                <div className="container-x pb-16 pt-40">
                    <Reveal>
                        <div className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.3em] text-sky">
                            <Crosshair className="text-sky" />
                            {c.contact.eyebrow}
                        </div>
                    </Reveal>
                    <h1 className="mt-6 text-[clamp(2.8rem,8vw,7rem)] font-light leading-[1.03] tracking-[-0.02em] text-paper">
                        <MaskLines mount lines={c.contact.lines} step={0.13} delay={0.2} />
                    </h1>
                    <Reveal delay={0.4}>
                        <p className="mt-6 max-w-md text-base leading-relaxed text-paper/70 sm:text-lg">
                            {c.contact.note}
                        </p>
                    </Reveal>
                </div>
            </section>

            <section className="container-x py-20 md:py-28" data-testid="contact-channels">
                <Reveal>
                    <a
                        href={`tel:${PHONE_TEL}`}
                        data-testid="contact-phone-link"
                        className="group block rounded-3xl border border-line bg-pure p-8 transition-all duration-300 hover:shadow-soft sm:p-12"
                    >
                        <div className="flex flex-wrap items-center justify-between gap-4">
                            <h2 className="flex items-center gap-3 font-mono text-[11px] font-normal uppercase tracking-[0.25em] text-sky-text">
                                <Phone className="h-4 w-4" />
                                {c.contact.phoneLabel}
                            </h2>
                            <span className="flex items-center gap-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-mist">
                                <span className="h-2 w-2 animate-pulse-dot rounded-full bg-sky-text" />
                                {c.contact.status}
                            </span>
                        </div>
                        <p className="mt-6 text-[clamp(1.8rem,5.5vw,4.2rem)] font-light tracking-tight text-ink transition-colors duration-300 group-hover:text-sky-text">
                            {PHONE}
                        </p>
                    </a>
                </Reveal>

                <Reveal delay={0.1}>
                    <a
                        href={`mailto:${EMAIL}`}
                        data-testid="contact-email-link"
                        className="group mt-5 block rounded-3xl border border-line bg-pure p-8 transition-all duration-300 hover:shadow-soft sm:p-12"
                    >
                        <h2 className="flex items-center gap-3 font-mono text-[11px] font-normal uppercase tracking-[0.25em] text-sky-text">
                            <Mail className="h-4 w-4" />
                            {c.contact.emailLabel}
                        </h2>
                        <p className="mt-6 text-[clamp(1.5rem,4.5vw,3.4rem)] font-light tracking-tight text-ink transition-colors duration-300 group-hover:text-sky-text">
                            {EMAIL}
                        </p>
                    </a>
                </Reveal>

                <div className="mt-6 grid gap-5 md:grid-cols-2 xl:grid-cols-5">
                    {c.contact.offices.map((o, i) => (
                        <Reveal key={o[0]} delay={0.12 + i * 0.06}>
                            <div className="flex h-full flex-col justify-between rounded-3xl border border-line bg-pure p-7" data-testid={`office-card-${i + 1}`}>
                                <h3 className="flex items-start gap-3 font-mono text-[10px] font-normal uppercase leading-relaxed tracking-[0.18em] text-sky-text">
                                    <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                                    {o[0]}
                                </h3>
                                <div className="mt-8">
                                    <p className="text-sm leading-relaxed text-ink">{o[1]}</p>
                                    <a
                                        href={`tel:${o[3]}`}
                                        data-testid={`office-phone-${i + 1}`}
                                        className="mt-4 block text-lg font-medium text-ink transition-colors hover:text-sky-text"
                                    >
                                        {o[2]}
                                    </a>
                                    <a
                                        href={`mailto:${o[4]}`}
                                        data-testid={`office-mail-${i + 1}`}
                                        className="mt-2 block font-mono text-xs text-mist transition-colors hover:text-sky-text"
                                    >
                                        {o[4]}
                                    </a>
                                </div>
                            </div>
                        </Reveal>
                    ))}
                    <Reveal delay={0.3}>
                        <div className="flex h-full flex-col justify-between rounded-3xl bg-ink p-7" data-testid="contact-markets-card">
                            <h2 className="flex items-center gap-3 font-mono text-[10px] font-normal uppercase tracking-[0.22em] text-sky">
                                <Globe className="h-3.5 w-3.5" />
                                {c.contact.areaTitle}
                            </h2>
                            <ul className="mt-8 space-y-3">
                                {c.contact.area.map((a) => (
                                    <li key={a} className="flex items-start gap-3 text-sm leading-relaxed text-paper/80">
                                        <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-sky" />
                                        {a}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </Reveal>
                </div>

                <Reveal delay={0.2}>
                    <p className="mt-10 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.18em] text-mist">
                        <span className="h-px w-8 bg-sky-text/50" />
                        {c.contact.yardLabel}: {c.contact.yard}
                    </p>
                </Reveal>
            </section>
        </>
    );
}
